<template>
  <div class="M-Flipper" :id="domId" :class="[flipType, theme, { go: isFlipping }]" :style="cardStyle">
    <div class="digital front" :class="_textClass(frontTextFromData)"></div>
    <div class="digital back" :class="_textClass(backTextFromData)"></div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, watch, nextTick } from "vue"
import { v4 } from "uuid"
import sleep from "@p-v/utils/sleep"
const isFlipping = ref(false)
const flipType = ref("down")
const frontTextFromData = ref("0")
const backTextFromData = ref("1")
const domId = ref(`uuid_${v4()}`)
const cardStyle = ref({})

const styleElement = ref<HTMLStyleElement | null>(null)

interface FlipperProps {
  frontText: number | string
  backText: number | string
  theme: string
  style: { [key: string]: string }
  duration: number
}

const props = withDefaults(defineProps<FlipperProps>(), {
  frontText: () => 0,
  backText: () => 1,
  theme: () => "",
  style: () => ({}),
  duration: () => 1000,
})

watch(
  () => props.frontText,
  async (now: number, before: number) => {
    if (now === before) return
    const newDuration = props.duration / Math.abs(now - (before as number) || 0)
    updatePseudoElementStyle(newDuration)
    nextTick(async () => {
      if (now > before) {
        let from: number = (before as number) || 0
        do {
          let next: number = from + 1
          flipUp(from.toString(), next.toString(), newDuration)
          from++
          await sleep(newDuration + 10)
        } while (from < now)
      } else {
        let from: number = (before as number) || 0
        do {
          let next: number = from - 1
          flipDown(from.toString(), next.toString(), newDuration)
          from--
          await sleep(newDuration + 10)
        } while (from > now)
      }
    })
  },
)

const _textClass = (text: number | string) => {
  return `number${text}`
}

const _flip = (type: string, front: string, back: string, duration?: number) => {
  // 如果处于翻转中，则不执行
  if (isFlipping.value) {
    return false
  }
  frontTextFromData.value = front
  backTextFromData.value = back
  // 根据传递过来的type设置翻转方向
  flipType.value = type
  // 设置翻转状态为true
  isFlipping.value = true
  setTimeout(() => {
    // 设置翻转状态为false
    isFlipping.value = false
    frontTextFromData.value = back
  }, duration)
}

const flipDown = (front: string, back: string, duration?: number) => {
  _flip("down", front, back, duration)
}

const flipUp = (front: string, back: string, duration?: number) => {
  _flip("up", front, back, duration)
}

const setFront = (text: string) => {
  frontTextFromData.value = text
}

const setBack = (text: string) => {
  backTextFromData.value = text
}

const updatePseudoElementStyle = (time: number) => {
  if (styleElement.value) {
    const sec = time / 1000
    const { background } = props.style
    let styleString = `
    #${domId.value} .digital:before,
    #${domId.value} .digital:after {
        background: ${background};
    }
    #${domId.value}.down.go .front:before {
        animation: frontFlipDown ${sec}s ease-in-out both;
    }
    #${domId.value}.down.go .back:after {
        animation: backFlipDown ${sec}s ease-in-out both;
    }
    #${domId.value}.up.go .front:after {
        animation: frontFlipUp ${sec}s ease-in-out both;
    }
    #${domId.value}.up.go .back:before {
        animation: backFlipUp ${sec}s ease-in-out both;
    }
    `
    styleElement.value.innerHTML = styleString
  }
}

onMounted(() => {
  styleElement.value = document.createElement("style")
  document.head.appendChild(styleElement.value)
  setFront(props.frontText.toString())
  setBack(props.backText.toString())
  const { background, border: borderOpt, height, ...style } = props.style
  updatePseudoElementStyle(props.duration)
  const border = borderOpt || `solid 1px ${background}`
  let lineHeight = height

  cardStyle.value = { ...style, height, lineHeight, border }
})

defineExpose({ flipDown, flipUp, setFront, setBack })
</script>

<style lang="scss">
.M-Flipper {
  display: inline-block;
  position: relative;
  width: 60px;
  height: 100px;
  line-height: 100px;
  border: solid 1px #000;
  border-radius: 10px;
  background: #fff;
  font-size: 66px;
  color: #fff;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.5);
  text-align: center;
  font-family: "Helvetica Neue";
}

.M-Flipper .digital:before,
.M-Flipper .digital:after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  background: #000;
  overflow: hidden;
  box-sizing: border-box;
}

.M-Flipper .digital:before {
  top: 0;
  bottom: 50%;
  border-radius: 10px 10px 0 0;
  border-bottom: solid 1px #666;
}

.M-Flipper .digital:after {
  top: 50%;
  bottom: 0;
  border-radius: 0 0 10px 10px;
  line-height: 0;
}

/*向下翻*/
.M-Flipper.down .front:before {
  z-index: 3;
}

.M-Flipper.down .back:after {
  z-index: 2;
  transform-origin: 50% 0%;
  transform: perspective(160px) rotateX(180deg);
}

.M-Flipper.down .front:after,
.M-Flipper.down .back:before {
  z-index: 1;
}

.M-Flipper.down.go .front:before {
  transform-origin: 50% 100%;
  /* animation: frontFlipDown var(--pft-data-flip-animation-duration, 0.6s) ease-in-out both; */
  box-shadow: 0 -2px 6px rgba(255, 255, 255, 0.3);
  backface-visibility: hidden;
}

/* .M-Flipper.down.go .back:after { */
/* animation: backFlipDown var(--pft-data-flip-animation-duration, 0.6s) ease-in-out both; */
/* } */

/*向上翻*/
.M-Flipper.up .front:after {
  z-index: 3;
}

.M-Flipper.up .back:before {
  z-index: 2;
  transform-origin: 50% 100%;
  transform: perspective(160px) rotateX(-180deg);
}

.M-Flipper.up .front:before,
.M-Flipper.up .back:after {
  z-index: 1;
}

.M-Flipper.up.go .front:after {
  transform-origin: 50% 0;
  /* animation: frontFlipUp 0.6s ease-in-out both; */
  box-shadow: 0 2px 6px rgba(255, 255, 255, 0.3);
  backface-visibility: hidden;
}

/* .M-Flipper.up.go .back:before {
  animation: backFlipUp var(--pft-data-flip-animation-duration, 0.6s) ease-in-out both;
} */

@keyframes frontFlipDown {
  0% {
    transform: perspective(160px) rotateX(0deg);
  }

  100% {
    transform: perspective(160px) rotateX(-180deg);
  }
}

@keyframes backFlipDown {
  0% {
    transform: perspective(160px) rotateX(180deg);
  }

  100% {
    transform: perspective(160px) rotateX(0deg);
  }
}

@keyframes frontFlipUp {
  0% {
    transform: perspective(160px) rotateX(0deg);
  }

  100% {
    transform: perspective(160px) rotateX(180deg);
  }
}

@keyframes backFlipUp {
  0% {
    transform: perspective(160px) rotateX(-180deg);
  }

  100% {
    transform: perspective(160px) rotateX(0deg);
  }
}

.M-Flipper {
  .number0:before,
  .number0:after {
    content: "0";
  }
  .number1:before,
  .number1:after {
    content: "1";
  }
  .number2:before,
  .number2:after {
    content: "2";
  }
  .number3:before,
  .number3:after {
    content: "3";
  }
  .number4:before,
  .number4:after {
    content: "4";
  }
  .number5:before,
  .number5:after {
    content: "5";
  }
  .number6:before,
  .number6:after {
    content: "6";
  }
  .number7:before,
  .number7:after {
    content: "7";
  }
  .number8:before,
  .number8:after {
    content: "8";
  }
  .number9:before,
  .number9:after {
    content: "9";
  }
}
.M-Flipper.iconfont {
  font-size: 66px;
  .number0:before,
  .number0:after {
    content: "\e61f";
  }
  .number1:before,
  .number1:after {
    content: "\e61e";
  }
  .number2:before,
  .number2:after {
    content: "\e621";
  }
  .number3:before,
  .number3:after {
    content: "\e627";
  }
  .number4:before,
  .number4:after {
    content: "\e626";
  }
  .number5:before,
  .number5:after {
    content: "\e620";
  }
  .number6:before,
  .number6:after {
    content: "\e622";
  }
  .number7:before,
  .number7:after {
    content: "\e623";
  }
  .number8:before,
  .number8:after {
    content: "\e624";
  }
  .number9:before,
  .number9:after {
    content: "\e625";
  }
}
</style>
